<script setup lang="ts">
/**
 * CookieSettingsModal - Privacy Preference Center
 * Allows users to customize their cookie preferences
 */
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCookieStore } from '@/stores/cookies'

const { t } = useI18n()
const cookieStore = useCookieStore()

// Local state for toggles
const functional = ref(cookieStore.consent.functional)
const performance = ref(cookieStore.consent.performance)
const targeting = ref(cookieStore.consent.targeting)

// Expanded sections
const expandedSections = ref<Record<string, boolean>>({})

function toggleSection(section: string) {
  expandedSections.value[section] = !expandedSections.value[section]
}

function savePreferences() {
  cookieStore.savePreferences({
    functional: functional.value,
    performance: performance.value,
    targeting: targeting.value,
  })
}

function acceptAll() {
  cookieStore.acceptAll()
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="cookieStore.isModalOpen"
        class="fixed inset-0 z-[60] flex items-center justify-center p-4"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/50"
          @click="cookieStore.closeModal"
        ></div>

        <!-- Modal -->
        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <!-- Header -->
          <div class="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 class="font-heading text-xl text-charcoal">{{ $t('cookies.privacy_center') }}</h2>
            <button
              @click="cookieStore.closeModal"
              class="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto p-6">
            <!-- Intro -->
            <p class="text-gray-600 text-sm mb-6">
              {{ $t('cookies.intro_text') }}
            </p>

            <RouterLink
              to="/cookies"
              class="text-primary-500 hover:text-primary-600 text-sm underline mb-6 inline-block"
              @click="cookieStore.closeModal"
            >
              {{ $t('cookies.more_info_link') }}
            </RouterLink>

            <!-- Cookie Categories -->
            <div class="space-y-4 mt-6">
              <!-- Strictly Necessary -->
              <div class="border border-gray-200 rounded-lg overflow-hidden">
                <div class="flex items-center justify-between p-4 bg-gray-50">
                  <div class="flex items-center gap-3">
                    <button
                      @click="toggleSection('necessary')"
                      class="text-gray-500 hover:text-charcoal transition-colors"
                    >
                      <svg
                        class="w-5 h-5 transition-transform"
                        :class="{ 'rotate-90': expandedSections['necessary'] }"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <span class="font-medium text-charcoal">{{ $t('cookies.necessary_title') }}</span>
                  </div>
                  <span class="text-sm text-green-600 font-medium">{{ $t('cookies.always_active') }}</span>
                </div>
                <div v-show="expandedSections['necessary']" class="p-4 text-sm text-gray-600 border-t border-gray-200">
                  {{ $t('cookies.necessary_desc') }}
                </div>
              </div>

              <!-- Functional -->
              <div class="border border-gray-200 rounded-lg overflow-hidden">
                <div class="flex items-center justify-between p-4 bg-gray-50">
                  <div class="flex items-center gap-3">
                    <button
                      @click="toggleSection('functional')"
                      class="text-gray-500 hover:text-charcoal transition-colors"
                    >
                      <svg
                        class="w-5 h-5 transition-transform"
                        :class="{ 'rotate-90': expandedSections['functional'] }"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <span class="font-medium text-charcoal">{{ $t('cookies.functional_title') }}</span>
                  </div>
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="functional"
                      class="sr-only peer"
                    />
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                  </label>
                </div>
                <div v-show="expandedSections['functional']" class="p-4 text-sm text-gray-600 border-t border-gray-200">
                  {{ $t('cookies.functional_desc') }}
                </div>
              </div>

              <!-- Performance -->
              <div class="border border-gray-200 rounded-lg overflow-hidden">
                <div class="flex items-center justify-between p-4 bg-gray-50">
                  <div class="flex items-center gap-3">
                    <button
                      @click="toggleSection('performance')"
                      class="text-gray-500 hover:text-charcoal transition-colors"
                    >
                      <svg
                        class="w-5 h-5 transition-transform"
                        :class="{ 'rotate-90': expandedSections['performance'] }"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <span class="font-medium text-charcoal">{{ $t('cookies.performance_title') }}</span>
                  </div>
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="performance"
                      class="sr-only peer"
                    />
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                  </label>
                </div>
                <div v-show="expandedSections['performance']" class="p-4 text-sm text-gray-600 border-t border-gray-200">
                  {{ $t('cookies.performance_desc') }}
                </div>
              </div>

              <!-- Targeting -->
              <div class="border border-gray-200 rounded-lg overflow-hidden">
                <div class="flex items-center justify-between p-4 bg-gray-50">
                  <div class="flex items-center gap-3">
                    <button
                      @click="toggleSection('targeting')"
                      class="text-gray-500 hover:text-charcoal transition-colors"
                    >
                      <svg
                        class="w-5 h-5 transition-transform"
                        :class="{ 'rotate-90': expandedSections['targeting'] }"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <span class="font-medium text-charcoal">{{ $t('cookies.targeting_title') }}</span>
                  </div>
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="targeting"
                      class="sr-only peer"
                    />
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                  </label>
                </div>
                <div v-show="expandedSections['targeting']" class="p-4 text-sm text-gray-600 border-t border-gray-200">
                  {{ $t('cookies.targeting_desc') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex flex-col sm:flex-row gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              @click="acceptAll"
              class="flex-1 px-4 py-2.5 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors"
            >
              {{ $t('cookies.accept_all') }}
            </button>
            <button
              @click="savePreferences"
              class="flex-1 px-4 py-2.5 border border-charcoal text-charcoal font-medium rounded-lg hover:bg-charcoal hover:text-white transition-colors"
            >
              {{ $t('cookies.save_preferences') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
